// The board's squares plus the highlight overlays, all instanced.
//
// Tiles are thin boxes rather than flat planes so they catch the key light and
// show an edge — under an orthographic camera that edge is a lot of what tells
// you the board is a solid object rather than a texture.

import {
  BoxGeometry,
  type BufferGeometry,
  CircleGeometry,
  Color,
  type Material,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  NoColorSpace,
  Object3D,
  PlaneGeometry,
  RingGeometry,
  TextureLoader
} from "three";

import { classicCells, fileOf, rankOf } from "src/shared/board";
import { BELT_DECK } from "./industryViews";
import { armyColor, PALETTE } from "./palette";
import { squareToWorld } from "./coords";
import { fadeBelowBoard, RISE_DEPTH, setTransparent } from "./riseFade";

const TILE_HEIGHT = 0.12;
/** A hair above the board, and above a belt deck when one is in the way. */
const MARKER_Y = 0.008;
export const MARKER_OVER_BELT = BELT_DECK + 0.03;

export type HighlightKind =
  | "selection"
  | "legalMove"
  /**
   * A square a piece will be *put on*, rather than one a piece may walk to.
   *
   * A forge's destinations, and the ground a Free Space tile would pave. Both
   * used to be handed to `legalMove`, so the board drew "step here" for
   * something that is not a move at all — and on a king, which is a foundry as
   * well as a piece, the two sets were on screen together in one colour.
   */
  | "spawn"
  | "capture"
  /**
   * A blow that will not kill (§27): the ring marks the piece being hit, in
   * amber rather than capture red, because clicking it shoves the target one
   * square instead of taking it. Split from `capture` so the board never
   * promises a kill the tables will not deliver.
   */
  | "shove"
  /**
   * The squares whose contents just changed (§29 U4) — a move's two squares,
   * an undo's diff, or a history step's. **A wash and nothing else**: the disc
   * and ring the last-move marker used to draw went with it, so one look means
   * "this is what changed" whatever caused it. **It does not fade**; the next
   * change replaces it.
   */
  | "changed"
  | "check"
  | "reAimTarget"
  | "promotionSquare"
  /**
   * A promotion square that belongs to one army (`Position.promotionOwner`).
   *
   * Two kinds rather than one, because a highlight's colour is a property of
   * its *kind* — the pool shares a material — and both lines are on the board
   * at once. Drawn in the army's own colour rather than a pair invented here:
   * `armyColor` is the single answer to "whose is that" for pieces, lanes and
   * pads, and a promotion line is the same question about a square.
   */
  | "promotionWhite"
  | "promotionBlack"
  | "extent";

const HIGHLIGHT_COLORS: Record<HighlightKind, number> = {
  selection: PALETTE.selection,
  legalMove: PALETTE.legalMove,
  spawn: PALETTE.spawn,
  capture: PALETTE.capture,
  shove: PALETTE.shove,
  changed: PALETTE.changed,
  check: PALETTE.check,
  reAimTarget: PALETTE.reAimTarget,
  promotionSquare: PALETTE.promotionSquare,
  promotionWhite: armyColor(0),
  promotionBlack: armyColor(1),
  extent: PALETTE.extent
};

/**
 * Chequer parity.
 *
 * `& 1` rather than `% 2`, because JavaScript's remainder keeps the sign of
 * its left operand: `-1 % 2` is `-1`, not `1`. Every square west or south of
 * the origin that should have been light came out dark, so a board that grew
 * into negative coordinates lost the chequer entirely and read as one slab.
 * Two's complement makes `&` the parity the board actually wants.
 */
export const isLight = (sq: number): boolean => ((fileOf(sq) + rankOf(sq)) & 1) === 1;

/** How far under the board a new square starts, and how long it takes to rise. */
/**
 * How many markers a highlight kind starts with.
 *
 * Enough for an ordinary selection without allocating, and no longer a
 * *ceiling*: `growHighlights` doubles past it. A queen alone can want sixty-
 * four — every slider runs to `RULES.sliderRange`, so eight rays of eight.
 */
const HIGHLIGHT_POOL = 32;

/**
 * Kinds drawn as a bare wash, with no marker over them.
 *
 * One so far, and it is the §29 U4 ruling: the change tint is a flat square
 * and nothing else. A ring would say "look here" about a square nothing is
 * being offered on, which is what the blue last-move disc was doing.
 */
const WASH_ONLY: ReadonlySet<HighlightKind> = new Set<HighlightKind>(["changed"]);

/**
 * How strong the change tint is.
 *
 * **Roughly half, and it was 0.3** — chess.com's last-move highlight sits
 * around there and the designer asked for that square by name. A tenth of a
 * wash over a board this busy is a square you have to go looking for, which is
 * the opposite of the job: the whole point of the tint is that it is found
 * without being looked for.
 *
 * It is also the *only* number here now. There is no fade: the tint stands
 * until something else changes, so a player who looks away and back can still
 * see what happened while they were not watching.
 */
const CHANGED_TINT = 0.5;

const RISE_SECONDS = 0.45;
/** Extra delay per square of distance from the old edge, so it ripples outward. */
const RISE_STAGGER = 0.07;

/**
 * `setCells`' answer when the board did not change.
 *
 * Shared and frozen rather than a fresh `[]`: this is the ordinary case — the
 * board is rebuilt on every sync and grows a handful of times a run — and a
 * caller must not be able to keep the array and write to it.
 */
const NOTHING_ARRIVING: readonly number[] = Object.freeze([]);

export class BoardTiles {
  readonly group = new Object3D();
  private highlights = new Map<HighlightKind, Mesh[]>();
  /** The square-filling wash behind each marker, one pool per kind. */
  private tints = new Map<HighlightKind, Mesh[]>();
  private shown = new Map<HighlightKind, readonly number[]>();
  /**
   * The origin each kind's arrows point away from, remembered alongside the
   * squares.
   *
   * Kept because markers are re-placed by things that are not a new selection —
   * a heap changing the lift, ground rising under them — and re-placing without
   * it would silently reset every arrow to due east. `shown` exists for exactly
   * the same reason and this is its other half.
   */
  private aimedFrom = new Map<HighlightKind, number | undefined>();
  private disposables: { dispose(): void }[] = [];
  private tileMeshes: InstancedMesh[] = [];
  private squares: number[] = [];
  /**
   * The one box every tile is an instance of, and the two colours.
   *
   * Built once and shared by every rebuild. They used to be built *inside*
   * `buildTiles` and pushed onto `disposables`, which is only drained when the
   * renderer is torn down — so every board growth leaked a geometry and two
   * materials, and a run that grows the board forever leaked forever. Nothing
   * about them depends on which squares exist, which is the tell.
   */
  private geometry = new BoxGeometry(1, TILE_HEIGHT, 1);
  private tileMaterials: MeshStandardMaterial[] = [true, false].map((light) =>
    // A square that is still under the board is not drawn. See `riseFade.ts`:
    // the tile carries the offset everything else adds, so it is the one thing
    // that must fade or the new ground slides up through a floor that is not
    // there yet.
    fadeBelowBoard(
      new MeshStandardMaterial({
        color: light ? PALETTE.lightSquare : PALETTE.darkSquare,
        roughness: 0.85,
        metalness: 0
      })
    )
  );
  /** How many instances each mesh has room for, which is not how many it draws. */
  private capacity: number[] = [0, 0];
  /**
   * What each highlight pool is made of, so it can be grown after the fact.
   *
   * Held rather than rebuilt: the geometries and materials are shared by every
   * mesh in a pool and are already on `disposables`, so growing one costs a
   * `Mesh` and nothing else.
   */
  private markerParts = new Map<
    HighlightKind,
    { marker: BufferGeometry; wash: BufferGeometry; material: Material; tintMaterial: Material }
  >();
  /** Per-square rise: 0 is fully under the board, 1 is seated. */
  private risen = new Map<number, number>();
  private delay = new Map<number, number>();
  private elapsed = 0;
  /** Told which squares carry a belt or a heap, so markers can clear them. */
  private lift: (square: number) => number = () => 0;
  /** The same for the ground itself: a belt deck lifts, a heap does not. */
  private deck: (square: number) => number = () => 0;

  constructor(cells: Iterable<number> = classicCells()) {
    this.buildTiles([...cells]);
    this.buildHighlightPool();
  }

  /**
   * Rebuild for a new set of squares, raising whatever is new.
   *
   * Instanced meshes are sized at construction, so a board that grew needs new
   * ones. That is fine — it happens when a stage turns over, not per frame.
   *
   * **Returns the squares that are arriving**, which this has to work out
   * anyway to stagger them. Empty when nothing changed, so a caller wanting to
   * mark the moment ground appears needs no second diff of its own — and the
   * first sync of a game returns the whole board, which is exactly what it is:
   * every tile rises.
   */
  setCells(cells: readonly number[]): readonly number[] {
    const next = new Set(cells);
    const known = new Set(this.squares);
    let changed = cells.length !== this.squares.length;
    if (!changed) for (const sq of cells) if (!known.has(sq)) { changed = true; break; }
    if (!changed) return NOTHING_ARRIVING;

    // Squares that were already there stay put; the new ones come up from
    // under the board, later the further they are from ground that existed.
    const arriving = cells.filter((sq) => !known.has(sq));
    for (const sq of arriving) {
      this.risen.set(sq, 0);
      // **Measured from the clock as it stands, not from zero.** `elapsed` is
      // shared by every rising square and this used to reset it on each
      // growth, so a second arrival restarted the rise of everything still in
      // flight — reported from play as a Free Space tile's fade starting over
      // when the next one was placed. Ground normally arrives a whole region
      // at a time, which is why it took a card that places one square at a
      // time to show it.
      //
      // Absolute delays instead: a square that arrived a moment ago keeps its
      // own start, and a new one is staggered from now.
      this.delay.set(sq, this.elapsed + this.distanceToKnown(sq, known) * RISE_STAGGER);
    }
    for (const sq of this.squares) {
      if (!next.has(sq)) {
        this.risen.delete(sq);
        this.delay.delete(sq);
      }
    }
    // Deliberately *not* reset — see the arriving loop above. It is a running
    // clock that delays are absolute against, and zeroing it is what dragged
    // in-flight squares back under the board.
    this.buildTiles([...cells]);
    return arriving;
  }

  /** Chebyshev distance to the nearest square that was already on the board. */
  private distanceToKnown(sq: number, known: ReadonlySet<number>): number {
    let best = 8;
    for (const other of known) {
      const distance = Math.max(
        Math.abs(fileOf(other) - fileOf(sq)),
        Math.abs(rankOf(other) - rankOf(sq))
      );
      if (distance < best) best = distance;
      if (best <= 1) break;
    }
    return best;
  }

  /**
   * How high a marker on this square has to float.
   *
   * A move indicator sits a hair above the board, which a belt deck at 0.41
   * buries completely — so the one square you most need to see you could move
   * to, because you built a lane across it, was the one square with no marker
   * on it.
   */
  setLift(lift: (square: number) => number, deck: (square: number) => number = lift): void {
    this.lift = lift;
    this.deck = deck;
    for (const kind of this.highlights.keys()) {
      this.setHighlight(kind, this.shown.get(kind) ?? [], this.aimedFrom.get(kind));
    }
  }

  /**
   * How far below its seated height this square currently sits, or 0.
   *
   * **Everything standing on a square rises with it.** A wave lands its army,
   * its scrap and any lane it brought on ground that is still coming up, so a
   * piece placed at y=0 hangs in the air over a hole for the length of the
   * animation and then the floor arrives underneath it. The offset is the
   * tile's own, so anything that adds it is welded to the deck.
   */
  riseOf(sq: number): number {
    const t = this.risen.get(sq);
    if (t === undefined) return 0;
    const eased = 1 - (1 - t) * (1 - t);
    return -(1 - eased) * RISE_DEPTH;
  }

  /**
   * Is any square off its seat — on its way up?
   *
   * The signal the fade is switched by, and it is the tiles' to give: they own
   * the offset that everything else adds, so they are the only thing that knows
   * when the board has finished arriving. `BoardRenderer` passes it on to the
   * pieces and the factory, which have no rise of their own to consult.
   *
   * `riseFade` is a function of world Y read per *vertex*, so the alpha is
   * already correct; all this decides is whether the renderer honours it.
   *
   * The cost is real and is why it is a signal rather than always on: a
   * transparent pass is a back-to-front sort of every instanced belt on the
   * board. A rise pays it for half a second.
   */
  rising(): boolean {
    return this.risen.size > 0;
  }

  /** The icon a kind draws with, or undefined for a plain disc or ring. */
  markerIcon(kind: HighlightKind): string | undefined {
    return BoardTiles.MARKER_ICONS[kind]?.icon;
  }

  /** Does this kind's picture point somewhere, or is it symmetric? */
  markerAims(kind: HighlightKind): boolean {
    return BoardTiles.MARKER_ICONS[kind]?.aim ?? false;
  }

  /** The geometry a kind's markers are made of, for tests. */
  markerGeometry(kind: HighlightKind): BufferGeometry | undefined {
    return this.markerParts.get(kind)?.marker;
  }

  /**
   * The Y rotation that aims a marker on `to` along the line from `from`.
   *
   * **World coordinates are the basis, never the camera.** This used to
   * counter-rotate by the camera's yaw to keep the picture screen-upright —
   * the right instinct for a *label* and wrong for a mark lying on the board,
   * because it made the arrow mean "that way across your monitor" instead of
   * "that way across the board". Turning the view then turned what the marker
   * was claiming, and two players reading one board from opposite ends could
   * not both be right. A bearing in world space says the same thing from every
   * seat and needs no per-frame work at all.
   *
   * **Derived from the world axes, never from `FACING_YAW`.** That constant
   * shipped backwards from Phase 3 until a playtest caught it, because every
   * test compared the renderer against the same wrong number — so this is
   * worked out from what `coords.ts` does, and `markers.test.ts` pins it
   * against the axes rather than against this function.
   *
   * The marker plane is `rotateX(-π/2)`, which leaves its local +X on world +X,
   * and `arrow_e` points along its own +X — so at zero rotation the arrow
   * already points **east**. A turn of φ about +Y carries +X to
   * `(cos φ, 0, -sin φ)`, so aiming at `(dx, dz)` wants `atan2(-dz, dx)`.
   */
  private bearing(from: number, to: number): number {
    const dx = fileOf(to) - fileOf(from);
    // **Rank climbs north and north is -Z.** `rankToZ` is `OFFSET - rank`, so a
    // move one rank north is a *negative* step in Z — the inversion that made
    // the old marker point at the board's south when the move went north.
    const dz = -(rankOf(to) - rankOf(from));
    if (dx === 0 && dz === 0) return 0;
    return Math.atan2(-dz, dx);
  }

  /** True while squares are still rising. Drives the render loop. */
  update(dt: number): boolean {
    if (this.risen.size === 0) return false;
    this.elapsed += dt;
    // On for the length of the rise and off the frame it settles. The shader
    // scales alpha either way; this is what makes the renderer honour it, and
    // leaving it on would put every tile on the board in the transparent pass
    // for the rest of the game.
    setTransparent(this.tileMaterials, true);

    let moving = false;
    for (const [sq, value] of this.risen) {
      const t = (this.elapsed - (this.delay.get(sq) ?? 0)) / RISE_SECONDS;
      const next = Math.min(1, Math.max(0, t));
      if (next !== value) this.risen.set(sq, next);
      if (next < 1) moving = true;
    }
    this.placeTiles();
    if (!moving) {
      this.risen.clear();
      this.delay.clear();
      setTransparent(this.tileMaterials, false);
    }
    // Markers ride their square up too, or a last-move ring left by the wave
    // that opened the ground floats over a hole until the floor catches it.
    for (const kind of this.highlights.keys()) {
      this.setHighlight(kind, this.shown.get(kind) ?? [], this.aimedFrom.get(kind));
    }
    // True on the settling frame as well as the moving ones, because that is
    // the frame the offset reaches zero — reporting `moving` here would leave
    // everything standing on the board a hair below its seat until something
    // else happened to redraw it.
    return true;
  }

  /**
   * Point the two meshes at a new set of squares, growing their buffers only
   * when the board actually outgrows them.
   *
   * An `InstancedMesh` fixes its capacity at construction, so a board that
   * grows past it does need a new one — but *capacity* is not *count*, and
   * conflating them meant reallocating both meshes on every change, including
   * the ones that shrink. Capacity doubles when it is exceeded, so a board
   * that grows a camp at a time reallocates a handful of times over a whole
   * run instead of once per stage, and never on the way down.
   */
  private buildTiles(cells: number[]): void {
    this.squares = cells;

    [true, false].forEach((light, index) => {
      const squares = cells.filter((sq) => isLight(sq) === light);
      const need = Math.max(1, squares.length);

      if (need > this.capacity[index]) {
        const old = this.tileMeshes[index];
        if (old) {
          this.group.remove(old);
          // Releases the instance buffers only — the geometry and material are
          // shared and outlive every mesh that has ever pointed at them.
          old.dispose();
        }
        // Doubling, so a board that creeps outward does not reallocate per step.
        const capacity = Math.max(need, this.capacity[index] * 2);
        const mesh = new InstancedMesh(this.geometry, this.tileMaterials[index], capacity);
        // Same reason as the industry meshes: the cached bounding sphere is
        // computed once, from an empty mesh, and never revisited. A board that
        // grows past it would vanish wholesale.
        mesh.frustumCulled = false;
        mesh.receiveShadow = true;
        this.tileMeshes[index] = mesh;
        this.capacity[index] = capacity;
        this.group.add(mesh);
      }

      const mesh = this.tileMeshes[index];
      mesh.count = squares.length;
      mesh.userData.squares = squares;
    });

    this.placeTiles();
  }

  /**
   * Write every tile's position, lifting the ones still on their way up.
   *
   * Eased rather than linear: a square that decelerates into place reads as
   * being *set down*, where a constant rise reads as a lift.
   */
  private placeTiles(): void {
    const matrix = new Matrix4();
    for (const mesh of this.tileMeshes) {
      const squares = mesh.userData.squares as number[];
      squares.forEach((sq, i) => {
        const p = squareToWorld(sq, -TILE_HEIGHT / 2 + this.riseOf(sq));
        mesh.setMatrixAt(i, matrix.makeTranslation(p.x, p.y, p.z));
      });
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  /**
   * A pool of overlay meshes per highlight kind, hidden when unused and
   * **grown when a selection outruns it**. Legal-move markers are discs; the
   * rest are rings that frame the square without hiding what stands on it.
   */
  /**
   * The icon each kind is drawn as, where a shape says more than a colour.
   *
   * **Only two, and that is the design rather than a start.** A marker's job is
   * to be read at a glance on a board PLAN §7.1 already calls crowded, and a
   * board where every kind is a different picture is the arrow soup that
   * warning is about. These two are the pair that were genuinely ambiguous: a
   * square you may *move to* and a square a piece will *appear on* were the
   * same disc in almost the same green, and on a foundry king both are offered
   * by one click. Everything else stays a disc or a ring.
   *
   * `aim` is what separates them, and it is a fact about the *picture*. An
   * arrow means nothing until it points somewhere, so it is turned to face
   * along the board; a cross is symmetric and means "here", so turning it would
   * be turning a thing with no front.
   */
  private static readonly MARKER_ICONS: Partial<
    Record<HighlightKind, { icon: string; aim: boolean }>
  > = {
    // The art points **east** and the mark is aimed from wherever the move
    // starts, so the arrow on each square lies along the line the piece would
    // travel. `arrow_e` rather than a neutral shape precisely because that
    // line is information the old disc threw away.
    legalMove: { icon: "arrow_e", aim: true },
    // A piece appearing is not a journey, so this one has no bearing to draw.
    spawn: { icon: "line_cross", aim: false }
  };

  private buildHighlightPool(): void {
    const disc = new CircleGeometry(0.16, 20).rotateX(-Math.PI / 2);
    const ring = new RingGeometry(0.4, 0.48, 24).rotateX(-Math.PI / 2);
    // The icons are drawn on a plate rather than on the wash: the wash fills
    // the square and is deliberately faint, and an icon at that opacity is
    // unreadable. Two thirds of a square, so it clears the piece standing
    // there at a 44-degree pitch without shrinking to a smudge when zoomed out.
    const plate = new PlaneGeometry(0.66, 0.66).rotateX(-Math.PI / 2);
    this.disposables.push(plate);
    // A tint that fills the square, under the marker.
    //
    // The ring alone asks the player to find a thin outline among the pieces,
    // the belts, the cargo and the heaps sharing those same 64-and-growing
    // squares — PLAN §7.1 calls that surface crowded and rates legibility the
    // number one risk. A washed square reads at a glance and at any zoom, and
    // it carries the one distinction that matters most: green is somewhere you
    // may go, red is something that will happen to a piece.
    const wash = new PlaneGeometry(0.98, 0.98).rotateX(-Math.PI / 2);
    this.disposables.push(disc, ring, wash);

    for (const kind of Object.keys(HIGHLIGHT_COLORS) as HighlightKind[]) {
      const colour = new Color(HIGHLIGHT_COLORS[kind]);
      const icon = BoardTiles.MARKER_ICONS[kind];
      const material = new MeshBasicMaterial({
        color: colour,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        // **The map multiplies the colour, which is why the art is white.** A
        // solid white silhouette tinted by `color` gives one file per shape
        // rather than one per shape per colour, and the marker keeps whatever
        // colour the palette says it is.
        ...(icon ? { alphaTest: 0.35 } : {})
      });
      if (icon) this.loadIcon(icon.icon, material);
      // Much fainter, because it covers a hundred times the area. At the
      // marker's opacity a legal-move square is a slab of colour and the piece
      // standing on it disappears.
      const tintMaterial = new MeshBasicMaterial({
        color: colour,
        transparent: true,
        opacity: kind === "changed" ? CHANGED_TINT : 0.24,
        depthWrite: false
      });
      this.disposables.push(material, tintMaterial);

      // The parts each pool is made of, kept so it can be grown later rather
      // than being a closure that only ran once at construction.
      this.markerParts.set(kind, {
        marker: icon ? plate : kind === "legalMove" ? disc : ring,
        wash,
        material,
        tintMaterial
      });
      this.highlights.set(kind, []);
      this.tints.set(kind, []);
      this.growHighlights(kind, HIGHLIGHT_POOL);
    }
  }

  /**
   * Fetch a marker icon and hang it on its material.
   *
   * **Asynchronous, and the marker is invisible until it lands.** `alphaTest`
   * is set at construction, and a material with an alpha test and no map has
   * nothing to test — three.js treats the missing map as opaque, so the plate
   * would draw as a solid coloured square for the few milliseconds before the
   * PNG arrives. That is a worse first frame than nothing, so the material
   * starts hidden and the load reveals it.
   *
   * **A miss leaves the marker hidden rather than square.** The failure this
   * guards is a renamed or unbuilt `public/markers/`, and the honest outcome
   * there is a board with no move dots and a line in the console — not one
   * covered in cyan tiles that look like a rendering bug in the game itself.
   */
  private loadIcon(icon: string, material: MeshBasicMaterial): void {
    material.visible = false;
    // **No document, no load.** `TextureLoader` goes through `ImageLoader`,
    // which reaches for `document.createElement("img")` — and the test env is
    // node, so constructing a board there would throw on a *texture fetch*,
    // which is a long way from anything a test about markers is asking. The
    // material stays hidden, which is what the pool draws with anyway until a
    // texture lands.
    if (typeof document === "undefined") return;
    new TextureLoader().load(
      `${import.meta.env.BASE_URL ?? "/"}markers/${icon}.png`,
      (texture) => {
        // The art is a white silhouette used as a stencil, so it is a *shape*
        // and not a colour: read as sRGB every partly-covered edge texel comes
        // back lifted, and the alpha test then bites in the wrong place.
        texture.colorSpace = NoColorSpace;
        material.map = texture;
        material.visible = true;
        material.needsUpdate = true;
        this.disposables.push(texture);
      },
      undefined,
      () => {
        console.warn(`board: marker icon "${icon}" did not load — run npm run assets`);
      }
    );
  }

  /**
   * Make sure this kind has `need` markers and washes to draw with.
   *
   * **Pools grow; they never truncate.** This was a fixed thirty-two per kind,
   * and `setHighlight` iterates the *meshes* — so a selection with more
   * squares than that simply stopped being drawn partway through, silently. A
   * queen in the open is what finds it: every slider runs to
   * `RULES.sliderRange`, so eight rays of eight is sixty-four legal moves, and
   * a board wide enough to give her the room showed about half of them. The
   * same rule `industryViews.fit` already lives under, arrived at the same way.
   *
   * Doubling rather than growing to fit exactly, so a selection that creeps up
   * a square at a time does not allocate on every click.
   */
  private growHighlights(kind: HighlightKind, need: number): void {
    const parts = this.markerParts.get(kind);
    if (!parts) return;
    const markers = this.highlights.get(kind) ?? [];
    const washes = this.tints.get(kind) ?? [];
    if (markers.length >= need) return;

    const want = Math.max(need, markers.length * 2, HIGHLIGHT_POOL);
    for (let i = markers.length; i < want; i++) {
      const marker = new Mesh(parts.marker, parts.material);
      marker.visible = false;
      marker.renderOrder = 2;
      // Which kind this belongs to, so a test can find it in the scene graph
      // rather than being handed the pool. `highlightCount` reads the *list*,
      // which is exactly how a pool that had silently stopped drawing kept
      // reporting the right number — so what a marker test wants is the mesh.
      marker.userData.kind = kind;
      this.group.add(marker);
      markers.push(marker);

      const wash = new Mesh(parts.wash, parts.tintMaterial);
      wash.visible = false;
      // Tagged like the marker, and tagged as a *wash* as well: the change
      // tint draws no marker at all, so a test that counted markers would
      // count nought however right or wrong the tint was.
      wash.userData.kind = kind;
      wash.userData.wash = true;
      // Under the marker and over the tile, so a ring on a tinted square
      // still reads as a ring rather than blending into its own wash.
      wash.renderOrder = 1;
      this.group.add(wash);
      washes.push(wash);
    }
    this.highlights.set(kind, markers);
    this.tints.set(kind, washes);
  }

  /**
   * Light the squares that just changed — **and only those** (§29 U4).
   *
   * It replaces rather than adds, and it **does not fade**: the tint stands
   * until the next change comes in, the way chess.com holds the last move's
   * two squares until the next move is played. A fade was the first draft and
   * the designer struck it out: a highlight that has gone is a highlight that
   * cannot answer "what happened while I was reading the pill".
   *
   * An empty list clears it, which is a claim in its own right — First is a
   * jump to a board nothing changed to reach, so nothing is lit. **A change
   * that touched no square passes nothing at all** rather than an empty list,
   * so `pass` leaves the last tint standing.
   *
   * Its own door rather than a bare `setHighlight` call so the two spellings
   * cannot drift, and so the one caller that means "what just changed" reads
   * as that at the call site.
   */
  setChanged(squares: readonly number[]): void {
    this.setHighlight("changed", squares);
  }

  /** How many squares a highlight is currently lighting. For the e2e smokes. */
  highlightCount(kind: HighlightKind): number {
    return this.shown.get(kind)?.length ?? 0;
  }

  setHighlight(
    kind: HighlightKind,
    squares: readonly number[],
    /**
     * The square an aiming marker points *away from* — the selected piece, for
     * legal moves. Ignored by every kind whose picture is symmetric, and
     * optional so the other twelve callers are unchanged.
     */
    from?: number
  ): void {
    this.shown.set(kind, squares);
    this.aimedFrom.set(kind, from);
    // Before anything is placed: the pool is what the loops below iterate, so
    // a short one is a selection that stops being drawn partway through.
    this.growHighlights(kind, squares.length);
    const meshes = this.highlights.get(kind) ?? [];
    // **An arrow has a front; a disc does not.** Every marker before these was
    // a circle or a ring, so which way round it lay was not a question worth
    // asking. It is now, and the answer is a compass bearing on the board — see
    // `bearing`. With no origin to measure from there is nothing to aim at, so
    // the arrow keeps the east its art was drawn pointing.
    const aims = this.markerAims(kind) && from !== undefined;
    const bare = WASH_ONLY.has(kind);
    meshes.forEach((mesh, i) => {
      const sq = squares[i];
      mesh.visible = !bare && sq !== undefined;
      if (sq === undefined || bare) return;
      squareToWorld(sq, MARKER_Y + this.lift(sq) + this.riseOf(sq), mesh.position);
      mesh.rotation.y = aims ? this.bearing(from, sq) : 0;
    });
    // The wash stays on the deck even where the marker is lifted clear of a
    // heap: it is a property of the *square*, and one floating at the height
    // of a nine-scrap pile would read as a hovering panel rather than as the
    // ground being lit.
    const tints = this.tints.get(kind) ?? [];
    tints.forEach((mesh, i) => {
      const sq = squares[i];
      mesh.visible = sq !== undefined;
      if (sq !== undefined)
        squareToWorld(sq, MARKER_Y * 0.5 + this.deck(sq) + this.riseOf(sq), mesh.position);
    });
  }

  clearHighlights(): void {
    for (const kind of this.highlights.keys()) this.setHighlight(kind, []);
  }

  dispose(): void {
    for (const item of this.disposables) item.dispose();
    this.disposables = [];
    for (const mesh of this.tileMeshes) mesh.dispose();
    this.tileMeshes = [];
    for (const material of this.tileMaterials) material.dispose();
    this.geometry.dispose();
    this.highlights.clear();
  }
}
